/**
 * CashConversionCycleCard Component
 * Visualizes CCC = WIP Days + AR Days - AP Days
 * Shows current vs target with formula breakdown
 */

import { StatusBadge } from './StatusBadge';
import { CalcTooltip } from './CalcTooltip';
import { CommentButton } from './CommentButton';
import type { CashConversionCycle } from '@/types/scalingUp';

interface CashConversionCycleCardProps {
  data: CashConversionCycle | null;
  loading: boolean;
  onInsight?: () => void;
}

function cccStatus(current: number, target: number): 'healthy' | 'warning' | 'critical' {
  if (current <= target) return 'healthy';
  if (current <= target * 2) return 'warning';
  return 'critical';
}

export function CashConversionCycleCard({ data, loading, onInsight }: CashConversionCycleCardProps) {
  if (loading && !data) {
    return (
      <div className="scaling-up__card">
        <h2 className="scaling-up__card-title">Cash Conversion Cycle</h2>
        <div className="skeleton" style={{ height: '100px' }} />
      </div>
    );
  }

  if (!data) return null;

  const status = cccStatus(data.totalCccDays, data.targetCccDays);

  return (
    <div className="scaling-up__card">
      <div className="scaling-up__card-header">
        <h2 className="scaling-up__card-title">
          Cash Conversion Cycle
          <CalcTooltip title="How CCC is calculated">
            <p><strong>Formula:</strong> <code>WIP + AR - AP</code></p>
            <p><strong>WIP Days</strong> ({data.wipDays}d): Estimated time from work done to invoice raised (configured).</p>
            <p><strong>AR Days</strong> ({data.arDays}d): Dollar-weighted average payment time from paid invoices.</p>
            <p><strong>AP Days</strong> ({data.apDays}d): Average time to pay supplier bills.</p>
            <p>Lower CCC = cash flows faster. Target: {data.targetCccDays}d.</p>
          </CalcTooltip>
        </h2>
        <StatusBadge status={status} label={`${data.totalCccDays} days`} size="sm" />
        {onInsight && (
          <button
            className="trend-chart__ai-btn"
            onClick={onInsight}
            title="AI Insights — CCC analysis"
            aria-label="AI Insights for Cash Conversion Cycle"
          >
            AI
          </button>
        )}
        <CommentButton targetType="metric" targetId="ccc" />
      </div>

      <div className="ccc">
        <div className="ccc__value">{data.totalCccDays} days</div>
        <div className="ccc__target">Target: {data.targetCccDays} days</div>

        <div className="ccc__formula" aria-label="CCC formula breakdown">
          <div className="ccc__component">
            <span className="ccc__component-value">{data.wipDays}</span>
            <span className="ccc__component-label">WIP Days</span>
          </div>
          <span className="ccc__operator">+</span>
          <div className="ccc__component">
            <span className="ccc__component-value">{data.arDays}</span>
            <span className="ccc__component-label">AR Days</span>
          </div>
          <span className="ccc__operator">&minus;</span>
          <div className="ccc__component">
            <span className="ccc__component-value">{data.apDays}</span>
            <span className="ccc__component-label">AP Days</span>
          </div>
          <span className="ccc__operator">=</span>
          <div className="ccc__component ccc__component--result">
            <span className="ccc__component-value">{data.totalCccDays}</span>
            <span className="ccc__component-label">CCC</span>
          </div>
        </div>
      </div>
    </div>
  );
}
